/**
 * Project Data Utility (post legacy-removal — 2026-04-28)
 *
 * 과거 이름은 "Project Data Migration Utility" 였으나, legacy
 * top-level `nodes/edges` → `models[]` 자동 wrap 이 silent 데이터 손실
 * 사고를 일으켜 마이그레이션 코드 일체 삭제. 현재 파일은 신구조
 * (`totalScope`, `models[]`, `updateHistory[]`)
 * 위에서만 동작하는 read-only helper 모음.
 */

import type { ProjectData, Model, ModelScope, Project, ProjectSummary } from '@/types/project';
import { RETIRED_MODEL_SETTINGS_KEYS, SAVED_APPEARANCE_UPGRADES, SAVED_PARAMETER_UPGRADES } from '@/domain/savedParameters';
import { isKnownScope, isRetiredScope, isScopeCovered, isScopeRestriction, migrateScopeIds, roleCountsForCoverage, scopeLabel } from '@/types/scope';

/**
 * 저장된 scope 의 **은퇴 id** 를 공식 id 로 바꾼다 — 카탈로그가 KAERI 공식 목록으로 바뀐 2026-10-01 전에
 * 저장된 프로젝트·모델이 옛 id(`primary/safety`·`primary/rhr`·`bop/switchyard`·`primary/rps`·`bop/ic`)를
 * 든다. 규칙은 카탈로그의 `RETIRED_SCOPE_IDS` 한 곳이고, 여기는 프로젝트 전체(totalScope·모델 scope)에 적용할
 * 뿐이다. 바뀐 것이 없으면 같은 객체를 돌려준다. 다음 저장 때 새 값이 기록된다.
 */
export function migrateProjectScopes(data: ProjectData): ProjectData {
  const touched = (ids: readonly string[] | undefined) => (Array.isArray(ids) ? ids : []).some(isRetiredScope);
  // models 가 배열이 아닌 손상 데이터는 손대지 않는다 — 그 모양은 projectStore 의 무결성 가드가 거절할 몫이다
  const models = Array.isArray(data.models) ? data.models : undefined;
  const needs = touched(data.totalScope?.systems) || (models ?? []).some((m) => touched(m?.scope?.systems));
  if (!needs) return data;
  return {
    ...data,
    totalScope: data.totalScope && touched(data.totalScope.systems)
      ? { ...data.totalScope, systems: migrateScopeIds(data.totalScope.systems) as ModelScope['systems'] }
      : data.totalScope,
    ...(models
      ? {
          models: models.map((m) =>
            m?.scope && touched(m.scope.systems)
              ? { ...m, scope: { ...m.scope, systems: migrateScopeIds(m.scope.systems) as ModelScope['systems'] } }
              : m,
          ),
        }
      : {}),
  };
}

/**
 * **불러온 저장본을 지금 꼴로** — 저장본을 읽는 자리(서비스의 불러오기·JSON 들여오기)가 이것 하나를 지난다: 범위의 은퇴 id(`migrateProjectScopes`),
 * 컴포넌트 매개변수의 옛 꼴(`SAVED_PARAMETER_UPGRADES`), 노드 외형의 옛 뜻(`SAVED_APPEARANCE_UPGRADES`), 모델 설정의 은퇴 키
 * (`RETIRED_MODEL_SETTINGS_KEYS`). 바뀐 것이 없으면 같은 객체를 돌려준다.
 * 다음 저장 때 새 값이 기록된다.
 */
export function migrateProjectData(data: ProjectData): ProjectData {
  const scoped = migrateProjectScopes(data);
  const models = Array.isArray(scoped.models) ? scoped.models : undefined;
  if (!models) return scoped;
  let changed = false;
  const retired = Object.keys(RETIRED_MODEL_SETTINGS_KEYS);
  const withoutRetiredSettings = (m: Model): Model => {
    const settings = (m as { settings?: unknown }).settings;
    if (!settings || typeof settings !== 'object' || !retired.some((k) => Object.prototype.hasOwnProperty.call(settings, k))) return m;
    changed = true;
    return { ...m, settings: Object.fromEntries(Object.entries(settings).filter(([k]) => !retired.includes(k))) } as Model;
  };
  const next = models.map(withoutRetiredSettings).map((m) => {
    type SavedNode = { data?: { componentType?: string; parameters?: Record<string, unknown>; appearance?: Record<string, unknown> } };
    const nodes = Array.isArray((m as { nodes?: unknown }).nodes) ? (m as { nodes: SavedNode[] }).nodes : undefined;
    if (!nodes) return m;
    let nodesChanged = false;
    // 칸마다 옛 꼴을 옮기는 표 — 매개변수·외형이 같은 규율(종류마다 한 번, 바뀐 것이 없으면 같은 객체)
    const upgradeField = (n: SavedNode, field: 'parameters' | 'appearance', table: Readonly<Record<string, (v: Record<string, unknown>) => Record<string, unknown>>>): SavedNode => {
      const upgrade = n?.data?.componentType ? table[n.data.componentType] : undefined;
      const v = n?.data?.[field];
      if (!upgrade || !v || typeof v !== 'object') return n;
      const next = upgrade(v);
      if (next === v) return n;
      nodesChanged = true;
      return { ...n, data: { ...n.data, [field]: next } };
    };
    const upgraded = nodes.map((n) => upgradeField(upgradeField(n, 'parameters', SAVED_PARAMETER_UPGRADES), 'appearance', SAVED_APPEARANCE_UPGRADES));
    if (!nodesChanged) return m;
    changed = true;
    return { ...m, nodes: upgraded } as Model;
  });
  return changed ? { ...scoped, models: next } : scoped;
}

/**
 * 프로젝트 데이터 유효성 검증
 *
 * 스코프는 배타(partition)가 아니라 cover 개념이다: 여러 모델이 같은
 * 계통을 겹쳐 담당해도 되고(겹침 허용), 대신 모델들의 union 이
 * 프로젝트 totalScope 를 빠짐없이 cover 해야 한다(구멍 금지).
 *
 * 2단계 scope(2026-08-18): 그룹(`primary`)은 그 그룹의 모든 계통을 담당하는
 * 뜻이고, 계통 전부를 각각 담당해도 그룹을 cover 한 것이다(isScopeCovered).
 * 역할이 제어기·시나리오인 참가자의 scope는 "작용 계통"이라 커버리지에서
 * 제외한다. 카탈로그 밖의 scope 문자열은 오류로 보고한다(무음 방치 금지).
 */
export function validateProjectData(data: ProjectData): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.models || data.models.length === 0) {
    return { valid: true, errors: [] }; // 모델 없으면 통과
  }

  // 계통 모델들이 담당(cover)하는 스코프 union — 겹침은 정상. ModelScope의 두 축
  // (systems·components)을 대칭으로 검증한다 — components 는 현재 UI 미사용
  // (항상 빈 배열)이지만 타입 계약에 있는 축이므로 커버리지 의미를 같게 둔다.
  const coveringSystems: string[] = [];
  const coveredComponents = new Set<string>();
  for (const model of data.models) {
    for (const system of model.scope?.systems ?? []) {
      if (!isKnownScope(system)) {
        errors.push(`Unknown scope id "${system}" in model "${model.name}" — 카탈로그에 없는 계통입니다`);
      }
    }
    if (!roleCountsForCoverage(model.role)) continue;
    coveringSystems.push(...(model.scope?.systems ?? []));
    for (const component of model.scope?.components ?? []) {
      coveredComponents.add(component);
    }
  }

  // 계통 모델의 담당은 프로젝트 scope 안에 있어야 한다 (UI 의 available 제한과 같은 불변식 —
  // 데이터 층에서도 지킨다). 프로젝트 scope 가 비어 있으면(미정) 검사하지 않는다.
  const projectScope = data.totalScope?.systems ?? [];
  if (isScopeRestriction(projectScope)) {
    for (const model of data.models) {
      if (!roleCountsForCoverage(model.role)) continue;
      for (const system of model.scope?.systems ?? []) {
        if (isKnownScope(system) && !isScopeCovered(system, projectScope)) {
          errors.push(`Model "${model.name}" scope "${scopeLabel(system)}" is outside the project scope`);
        }
      }
    }
  }

  // totalScope 의 모든 계통·컴포넌트가 최소 한 계통 모델에 담당되는지 (커버리지 보장)
  for (const system of data.totalScope?.systems ?? []) {
    if (!isKnownScope(system)) {
      errors.push(`Unknown scope id "${system}" in totalScope — 카탈로그에 없는 계통입니다`);
      continue;
    }
    if (!isScopeCovered(system, coveringSystems)) {
      errors.push(`Uncovered system scope: "${scopeLabel(system)}" — 담당 모델이 없습니다`);
    }
  }
  for (const component of data.totalScope?.components ?? []) {
    if (!coveredComponents.has(component)) {
      errors.push(`Uncovered component scope: "${component}" — 담당 모델이 없습니다`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * 모델 ID로 모델 찾기
 */
export function findModelById(data: ProjectData, modelId: string): Model | undefined {
  return data.models?.find((m) => m.id === modelId);
}

/**
 * 프로젝트의 총 노드 수 계산
 */
/**
 * 본문을 가진 프로젝트 → 목록 항목(ProjectSummary). 서버(BFF ListProjects)가 jsonb 에서 세는 규칙과 같다 —
 * 생성·수정 직후 목록을 다시 받지 않고 갱신할 때 쓴다. 실행 수는 본문에서 파생되지 않는다(서버가 실행 기록에서 센다) —
 * 고치기 전 항목(`previous`)의 값을 잇고, 새 프로젝트면 0.
 */
export function summarizeProject(project: Project, previous?: Pick<ProjectSummary, 'simulation_count'>): ProjectSummary {
  const models = project.data?.models ?? [];
  return {
    id: project.id,
    user_id: project.user_id,
    name: project.name,
    description: project.description,
    created_at: project.created_at,
    updated_at: project.updated_at,
    workspace_id: project.workspace_id,
    model_count: models.length,
    node_count: getTotalNodeCount(project.data ?? {}),
    simulation_count: previous?.simulation_count ?? 0,
    analysis_codes: models.map((m: Model) => (m.analysisCodes ?? []).join('/')).filter(Boolean),
  };
}

export function getTotalNodeCount(data: ProjectData): number {
  if (!data.models || data.models.length === 0) {
    return 0;
  }
  return data.models.reduce((sum, model) => sum + (model.nodes?.length ?? 0), 0);
}

/**
 * 프로젝트의 총 엣지 수 계산
 */
export function getTotalEdgeCount(data: ProjectData): number {
  if (!data.models || data.models.length === 0) {
    return 0;
  }
  return data.models.reduce((sum, model) => sum + (model.edges?.length ?? 0), 0);
}
